<template>
  <v-date-picker
    :min-date="minDate"
    :max-date="maxDate"
    :value="value"
    :popover="{
      visibility: disabled ? 'hidden' : 'click',
      placement: placement,
      positionFixed: positionFixed,
    }"
    :class="{ ['crm-fluid']: fluid }"
    @input="datePickerEvents($event)"
  >
    <template v-slot:default="{ inputProps, inputEvents, hidePopover }">
      <div
        :class="['date-picker', { 'date-picker-error': error }]"
        :disabled="disabled"
      >
        <input
          v-mask="maskDate"
          v-bind="inputProps"
          :class="{
            'with-clear-button': showClearButton,
            'error': localError,
            'is-input-disabled': isInputDisabled,
          }"
          :maxlength="maxlength"
          :placeholder="placeholder"
          :disabled="disabled"
          class="crm-input"
          v-on="inputEvents"
          @input="handleDateInput(inputProps.value, $event.target.value)"
          @click.stop="hidePopover"
        >
        <icon-close
          v-if="showClearButton"
          class="crm-close-icon"
          @click="() => {
            hidePopover();
            $emit('change', null);
          }"
        />
        <div
          :class="['crm-icon-wrapper', {
            'error': localError,
          }]"
        >
          <icon-calendar />
        </div>
        <template v-if="localError && errors.length">
          <div
            :class="['errors', {'errors-top': isErrorsTop}]"
          >
            <span
              class="crm-error-text"
            >
              {{ errors[0] }}
            </span>
          </div>
        </template>
      </div>
    </template>
  </v-date-picker>
</template>

<script>
import IconCalendar from 'assets/images/calendar-2.svg';
import IconClose from 'assets/images/clear_icon.svg';

export default {
  name: 'BaseDatePicker',
  components: {
    IconCalendar,
    IconClose,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Date,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'дд.мм.гггг',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    minDate: {
      type: Date,
      default: null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    fluid: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    positionFixed: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    maskDate: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: null,
    },
    isErrorsTop: {
      type: Boolean,
      default: false,
    },
    validateOnInput: {
      type: Boolean,
      default: false,
    },
    isInputDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localError: this.error,
    };
  },
  computed: {
    showClearButton() {
      return this.value !== null && this.clearable;
    },
  },
  methods: {
    handleDateInput(value, target) {
      const parsedTarget = this.parseDate(target);
      const parsedDate = this.parseDate(value);
      // если дата неполная — сбрасываем range ошибки
      if (target.length !== 10) {
        this.localError = false;
        this.$emit('change', null);
        this.$emit('changeInputValue', null);
        this.$emit('invalid-date', false);
        return;
      }
      const isOutOfRange =
      (this.minDate && parsedTarget < this.minDate ) ||
      (this.maxDate && parsedTarget > this.maxDate);
      if (isOutOfRange) {
        this.localError = true;
        this.$emit('change', parsedTarget);
        this.$emit('changeInputValue', parsedTarget);
        this.$emit('invalid-date');
      } else {
        this.localError = false;
        this.$emit('invalid-date', false);
      }
      if (!this.validateOnInput) return;

      this.localError = this.error;

      if ((this.maxDate && parsedDate > this.maxDate) || (this.minDate && parsedDate < this.minDate)) {
        this.localError = true;
      }

      this.$emit('validateInputValue', this.localError);
      this.$emit('invalid-date');
      return this.localError;


    },
    datePickerEvents(event) {
      if (event === null) {
        if (this.value) {
          const isOutOfRange =
            (this.minDate && this.value < this.minDate) ||
            (this.maxDate && this.value > this.maxDate);

          if (isOutOfRange) {
            this.localError = true;
            this.$emit('invalid-date');
          } else {
            this.localError = false;
            this.$emit('invalid-date', false);
            this.$emit('change', event);
          }
        }
        return;
      }

      this.localError = false;

      this.$emit('invalid-date', false);
      this.$emit('change', event);
    },
    parseDate(dateString) {
      const [day, month, year] = dateString.split('.').map(Number);
      if (!day || !month || !year) return null;

      const parsed = new Date(year, month - 1, day);

      if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
      ) {
        return null;
      }

      return parsed;
    }
  },
};
</script>

<style lang="scss" scoped>
.date-picker {
  position: relative;
  display: flex;
  height: 40px;
  width: 100%;
  cursor: pointer;
  border: 1px solid transparent;

  &[disabled] {
    cursor: not-allowed;
    background-color: $gray-disabled;
  }
  &-error {
    border-color: $red;
  }
}
.crm-fluid {
  width: 100%;
}
.crm-input {
  @include base-input;
  padding: 11px 15px 11px;
  width: calc(100% - 25px);
  &[disabled] {
    cursor: not-allowed;
    background-color: $gray-disabled;
  }
}
.crm-icon-wrapper {
  padding: 10px;
  background-color: $white;
  border-radius: 4px;
  border: 1px solid $gray;
  margin-left: -5px;
}
.crm-close-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  right: 46px;
}
.error {
  border-color: $red;
}
.errors {
  position: absolute;
  top: calc(100% + 10px);
  z-index: 1;
  &-top {
    top: 100%;
  }
  .crm-error-text {
    display: block;
    font-size: 12px;
    color: $red;
    font-weight: 500;
  }
}
.is-input-disabled {
  pointer-events: none;
}
</style>
